/**
 * Kennungen werden **auf dem Client** erzeugt, nicht in der Datenbank.
 *
 * Das ist keine Stilfrage: Offline muss die App einen Datensatz vollständig
 * anlegen können, samt Verweisen darauf (ein Satz gehört zu einem Workout, der
 * noch nie einen Server gesehen hat). Eine vom Server vergebene Kennung gibt es in
 * dem Moment nicht.
 */

/** UUID v4. `crypto.randomUUID` gibt es in Node ≥ 19 und in allen Zielbrowsern. */
export function newId(): string {
  return globalThis.crypto.randomUUID();
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isId(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * Feste Kennung aus einem Schlüssel — gleiche Eingabe, gleiche UUID, auf jedem
 * Gerät und im Scheduler.
 *
 * Für Zeilen, die zwei Seiten unabhängig voneinander anlegen können, etwa ein
 * Trainingstermin der Wochenregel: Das Telefon sagt ihn ab, der Scheduler legt
 * ihn zur Erinnerung an. Mit Zufallskennungen wären das zwei Zeilen für einen
 * Termin; so ist es dieselbe, und der Abgleich führt sie zusammen.
 *
 * Kein kryptografischer Hash — die Eindeutigkeit innerhalb eines Kontos genügt
 * (128 Bit aus vier unabhängigen FNV-1a-Läufen), Geheimnis ist keins im Spiel.
 */
export function stableId(key: string): string {
  const parts: string[] = [];
  for (let seed = 0; seed < 4; seed += 1) {
    let hash = (0x811c9dc5 ^ (seed * 0x9e3779b1)) >>> 0;
    for (let index = 0; index < key.length; index += 1) {
      hash ^= key.charCodeAt(index);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    parts.push(hash.toString(16).padStart(8, '0'));
  }
  const hex = parts.join('');
  // Version 8 („eigenes Verfahren"), Variante RFC 4122 — damit `isId` sie annimmt.
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `8${hex.slice(13, 16)}`,
    `${((parseInt(hex.slice(16, 17), 16) & 0x3) | 0x8).toString(16)}${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}
